import React, { useEffect, useState } from "react";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import PageLoader from "@/components/loader/PageLoader";
import Spinner from "@/components/loader/Spinner";
import DateFilterButton from "@/components/layouts/DateFilterButton";
import { format, startOfDay, endOfDay } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import TransactionActions from "./TransactionActions";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "@/assets/css/responsive.css";

interface Transaction {
  id: number;
  order_id: number;
  order_number: string;
  customer_name: string;
  branch_id: number;
  branch_name: string;
  amount: string;
  subtotal: string;
  tax: string;
  retref: string;
  account: string;
  tip: string;
  payment_type: string;
  employee: string | null;
  amount_refunded: string;
  status: string;
  created_at: string;
  card_type: string;
  last_four: string;
  resptext: string;
  authcode: string;
  token: string;
  bintype: string;
}

const TransactionTable: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [axiosInstance, isFetching] = useAxiosInstance();
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [branchTimezone, setBranchTimezone] = useState<string>("UTC");
  const { t } = useTranslation();

  useEffect(() => {
    const storedData = localStorage.getItem("user_data");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      const { selectedBranch } = parsedData;
      setSelectedBranch(selectedBranch.id);
      setBranchTimezone(selectedBranch.timezone || "UTC");
    }
  }, []);

  const formatToUTC = (
    date: Date | null,
    isEndOfDay: boolean = false,
  ): string => {
    if (!date) return "";
    const zonedDate = toZonedTime(date, branchTimezone);
    const localDate = isEndOfDay ? endOfDay(zonedDate) : startOfDay(zonedDate);
    return new Date(
      localDate.toLocaleString("en-US", {
        timeZone: branchTimezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }),
    )
      .toISOString()
      .slice(0, 19);
  };

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    const zonedDate = toZonedTime(date, branchTimezone);
    return format(zonedDate, "yyyy-MM-dd HH:mm:ss");
  };

  const fetchTransactions = async () => {
    setLoading(true);
    setError(null);

    try {
      const start = formatToUTC(startDate);
      const end = formatToUTC(endDate, true);
      const response = await axiosInstance.get(
        `api/v1/transaction/?branch=${selectedBranch}&created_at_after=${start}&created_at_before=${end}&page=${currentPage}&page_size=${pageSize}`,
      );

      if (!response.status.toString().startsWith("2")) {
        throw new Error("Failed to fetch transactions");
      }

      setTransactions(response.data.results);
      setTotalTransactions(response.data.count);
      setTotalPages(Math.ceil(response.data.count / pageSize));
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBranch) {
      fetchTransactions();
    }
  }, [selectedBranch, currentPage, pageSize, startDate, endDate]);

  const getTransactionStatusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case "success":
        return "bg-green-100 text-green-800";
      case "voided":
        return "bg-red-100 text-red-800";
      case "refunded":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTransactionBorderClass = (status: string) => {
    switch (status.toLowerCase()) {
      case "success":
        return "border-l-4 border-green-500";
      case "voided":
        return "border-l-4 border-red-500";
      case "refunded":
        return "border-l-4 border-yellow-500";
      default:
        return "border-l-4 border-gray-500";
    }
  };

  // Helper function to generate pagination numbers with ellipsis
  const getPageNumbers = (currentPage: number, totalPages: number) => {
    const delta = 1;
    const range = [];
    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }
    if (currentPage - delta > 2) {
      range.unshift("...");
    }
    if (currentPage + delta < totalPages - 1) {
      range.push("...");
    }
    range.unshift(1);
    if (totalPages > 1) {
      range.push(totalPages);
    }
    return range;
  };

  return (
    <PageLoader isFetching={isFetching}>
      <div className="min-h-screen p-4">
        <div className="mt-4 flex flex-col items-center justify-between space-y-2 sm:flex-row sm:space-y-0">
          <div className="mb-4 flex w-full flex-col items-center space-y-2 sm:flex-row sm:space-x-4 sm:space-y-0">
            <DateFilterButton
              onStartDateChange={(date) => setStartDate(date)}
              onEndDateChange={(date) => setEndDate(date)}
              startDate={startDate}
              endDate={endDate}
              labelText="Filter by date"
            />
          </div>
        </div>

        {loading ? (
          <Spinner />
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : selectedBranch ? (
          <>
            {/* Desktop Table */}
            <div className="desktop-layout rounded-lg bg-white p-4 shadow">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.transaction.reference")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.transaction.orderNumber")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.transaction.customer")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.transaction.cardInfo")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.transaction.paymentType")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.transaction.status")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.transaction.amount")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.transaction.date")}
                      </th>
                      <th className="px-6 py-3 text-left text-sm font-medium uppercase tracking-wider text-gray-500">
                        {t("back.management.transaction.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {transactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          {transaction.retref}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          {transaction.order_number}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          {transaction.customer_name}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          {transaction.card_type && `${transaction.card_type} `}
                          {transaction.last_four &&
                            `****${transaction.last_four}`}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          {transaction.payment_type}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          <span
                            className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${getTransactionStatusClass(transaction.status)}`}
                          >
                            {transaction.status}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          ${transaction.amount}
                          {transaction.amount_refunded !== "0.00" && (
                            <div className="text-xs text-red-500">
                              {t("back.management.transaction.refunded")}: ${transaction.amount_refunded}
                            </div>
                          )}
                          {transaction.tip !== "0.00" && (
                            <div className="text-xs text-gray-500">
                              {t("back.management.transaction.tip")}: ${transaction.tip}
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          {formatDateTime(transaction.created_at)}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">
                          <TransactionActions
                            transaction={transaction}
                            onActionComplete={fetchTransactions}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex justify-between">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="rounded bg-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-400"
                >
                  {t("shared.previous")}
                </button>
                <span>
                  {t("back.management.transaction.page")} {currentPage} {t("back.management.transaction.of")} {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="rounded bg-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-400"
                >
                  {t("shared.next")}
                </button>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="ml-4 w-16 rounded border p-2"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </div>

            {/* Mobile Layout */}
            <div className="mobile-layout">
              <div className="grid grid-cols-1 gap-4">
                {transactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className={`rounded-lg bg-white p-4 shadow ${getTransactionBorderClass(transaction.status)}`}
                  >
                    <div className="mb-4 flex items-center justify-between">
                      <div className="flex-1">
                        <div className="mb-1">
                          <span className="text-sm font-semibold text-gray-600">
                            {t("back.management.transaction.reference")}:
                          </span>
                          <span className="ml-2 text-sm">
                            {transaction.retref}
                          </span>
                        </div>
                        <div className="mb-1">
                          <span className="text-sm font-semibold text-gray-600">
                            {t("back.management.transaction.orderNumber")}:
                          </span>
                          <span className="ml-2 text-sm">
                            {transaction.order_number}
                          </span>
                        </div>
                      </div>
                      <div>
                        <span
                          className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${getTransactionStatusClass(transaction.status)}`}
                        >
                          {transaction.status}
                          {transaction.amount_refunded !== "0.00" &&
                            " (Refunded)"}
                        </span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="mb-1">
                        <span className="text-sm font-semibold text-gray-600">
                          {t("back.management.transaction.customer")}:
                        </span>
                        <span className="ml-2 text-sm">
                          {transaction.customer_name}
                        </span>
                      </div>
                      <div className="mb-1">
                        <span className="text-sm font-semibold text-gray-600">
                          {t("back.management.transaction.cardInfo")}:
                        </span>
                        <span className="ml-2 text-sm">
                          {transaction.card_type && `${transaction.card_type} `}
                          {transaction.last_four &&
                            `****${transaction.last_four}`}
                        </span>
                      </div>
                      <div className="mb-1">
                        <span className="text-sm font-semibold text-gray-600">
                          {t("back.management.transaction.paymentType")}:
                        </span>
                        <span className="ml-2 text-sm">
                          {transaction.payment_type}
                        </span>
                      </div>
                      <div className="mb-1">
                        <span className="text-sm font-semibold text-gray-600">
                          {t("back.management.transaction.amount")}:
                        </span>
                        <span className="ml-2 text-sm">
                          ${transaction.amount}
                        </span>
                        {transaction.amount_refunded !== "0.00" && (
                          <span className="ml-2 text-xs text-red-500">
                            ({t("back.management.transaction.refunded")}: ${transaction.amount_refunded})
                          </span>
                        )}
                      </div>
                      {transaction.tip !== "0.00" && (
                        <div className="mb-1">
                          <span className="text-sm font-semibold text-gray-600">
                            {t("back.management.transaction.tip")}:
                          </span>
                          <span className="ml-2 text-sm">
                            ${transaction.tip}
                          </span>
                        </div>
                      )}
                      <div className="mb-1">
                        <span className="text-sm font-semibold text-gray-600">
                          {t("back.management.transaction.date")}:
                        </span>
                        <span className="ml-2 text-sm">
                          {formatDateTime(transaction.created_at)}
                        </span>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <TransactionActions
                        transaction={transaction}
                        onActionComplete={fetchTransactions}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-col items-center space-y-4">
                <div className="text-sm text-gray-700">
                  {t("back.management.transaction.showing")} {(currentPage - 1) * pageSize + 1} {t("back.management.transaction.to")}{" "}
                  {Math.min(currentPage * pageSize, totalTransactions)} {t("back.management.transaction.of")}{" "}
                  {totalTransactions} {t("back.management.transaction.results")}
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="rounded p-2 text-gray-600 disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>

                  <div className="flex items-center space-x-2">
                    {getPageNumbers(currentPage, totalPages).map(
                      (page, index) => (
                        <button
                          key={index}
                          onClick={() =>
                            typeof page === "number" && setCurrentPage(page)
                          }
                          className={`rounded px-3 py-1 ${
                            currentPage === page
                              ? "bg-red-500 text-white"
                              : "bg-gray-200 text-gray-700"
                          }`}
                          disabled={typeof page !== "number"}
                        >
                          {page}
                        </button>
                      ),
                    )}
                  </div>

                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="rounded p-2 text-gray-600 disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>

                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="rounded border p-2"
                >
                  <option value={5}>{t("back.management.transaction.perPage", { count: 5 })}</option>
                  <option value={10}>{t("back.management.transaction.perPage", { count: 10 })}</option>
                  <option value={20}>{t("back.management.transaction.perPage", { count: 20 })}</option>
                </select>
              </div>
            </div>
          </>
        ) : (
          <p>{t("back.management.order.selectBranch")}</p>
        )}
      </div>
    </PageLoader>
  );
};

export default TransactionTable;
